import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Newspaper, 
  Download, 
  Camera,
  Users,
  Award,
  Star,
  ArrowRight,
  ExternalLink,
  Calendar
} from 'lucide-react';

const PressPage = () => {
  const pressReleases = [
    {
      date: "15 Novembre 2024",
      title: "PRIVIA lance son service de villas émotionnelles en Méditerranée",
      excerpt: "La plateforme luxury révolutionne l'expérience villa en associant chaque destination à une émotion spécifique.",
      category: "Lancement Produit",
      downloadLink: "#"
    },
    {
      date: "03 Octobre 2024", 
      title: "PRIVIA remporte le prix 'Innovation Luxury Travel 2024'",
      excerpt: "Reconnaissance internationale pour notre approche unique du voyage émotionnel personnalisé.",
      category: "Récompense",
      downloadLink: "#"
    },
    {
      date: "22 Août 2024",
      title: "Expansion PRIVIA : 50 nouvelles villas d'exception",
      excerpt: "Croissance de notre collection avec des propriétés exclusives en Grèce et en Espagne.",
      category: "Expansion",
      downloadLink: "#"
    }
  ];

  const mediaKit = [
    {
      type: "Logos & Identité",
      description: "Logos haute résolution, guidelines de marque",
      format: "ZIP (15 MB)",
      icon: Award
    },
    {
      type: "Photos Produits",
      description: "Images villas, expériences, équipe",
      format: "ZIP (125 MB)",
      icon: Camera
    },
    {
      type: "Données Entreprise",
      description: "Fact sheet, statistiques, biographies",
      format: "PDF (2 MB)",
      icon: Newspaper
    }
  ];

  const mediaContacts = [
    {
      name: "Marie Dubois",
      role: "Directrice Communication",
      email: "presse@priviacollection.com",
      phone: "+1 786 840 0745",
      specialties: ["Strategy", "Product Launches"]
    },
    {
      name: "James Wilson",
      role: "International PR Manager",
      email: "pr@priviacollection.com", 
      phone: "+377 97 70 00 00",
      specialties: ["European Markets", "Luxury Travel"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge 
            variant="outline" 
            className="mb-6 px-6 py-3 bg-white/50 backdrop-blur-sm border-gray-200 text-gray-700 text-sm font-light uppercase tracking-wider"
          >
            <Newspaper className="w-4 h-4 mr-2" />
            Espace Presse
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Actualités PRIVIA
          </h1>
          
          <p 
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Découvrez nos dernières actualités, ressources média et contactez notre équipe presse
          </p>

          <Button 
            className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-3 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Kit Média Complet
            <Download className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Press Releases */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-light mb-12 text-gray-800 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Communiqués de Presse
          </h2>

          <div className="space-y-6">
            {pressReleases.map((release, index) => (
              <Card key={index} className="p-8 hover:shadow-lg transition-all duration-300">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <Badge variant="outline" className="text-xs">
                        {release.category}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>{release.date}</span>
                      </div>
                    </div>
                    
                    <h3 
                      className="text-xl font-light text-gray-800 mb-3"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {release.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed">
                      {release.excerpt}
                    </p>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button variant="outline" size="sm">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Lire
                    </Button>
                    <Button variant="outline" size="sm">
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Media Kit */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Kit Média
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Toutes les ressources nécessaires pour vos articles et reportages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mediaKit.map((kit, index) => {
              const IconComponent = kit.icon;
              return (
                <Card key={index} className="p-6 text-center hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-amber-600" />
                  </div>
                  
                  <h3 className="text-lg font-medium text-gray-800 mb-3">
                    {kit.type}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4">
                    {kit.description}
                  </p>
                  
                  <p className="text-xs text-gray-500 mb-6">
                    {kit.format}
                  </p>
                  
                  <Button 
                    size="sm"
                    className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Media Contacts */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Contacts Presse
            </h2>
            <p className="text-lg text-gray-600">
              Notre équipe communication est à votre disposition
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mediaContacts.map((contact, index) => (
              <Card key={index} className="p-8 hover:shadow-lg transition-all duration-300">
                <div className="text-center mb-6">
                  <h3 
                    className="text-xl font-light text-gray-800 mb-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {contact.name}
                  </h3>
                  <p className="text-amber-600 font-medium text-sm mb-4">
                    {contact.role}
                  </p>
                </div>
                
                <div className="space-y-3 text-sm text-gray-600">
                  <div>
                    <strong>Email:</strong> {contact.email}
                  </div>
                  <div>
                    <strong>Téléphone:</strong> {contact.phone}
                  </div>
                  <div>
                    <strong>Spécialités:</strong> {contact.specialties.join(", ")}
                  </div>
                </div>
                
                <Button 
                  className="w-full mt-6 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
                >
                  Contacter
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressPage;